// One-shot WebAudio tones — no audio assets, feature-detected, best-effort:
// any failure is a silent no-op (a confirmation sound is never worth an
// error). Autoplay reality: the context starts suspended until the page has
// had a user gesture; every caller here fires after taps (PTT release, a
// modal opened by button), so resume() is enough — if it stays suspended the
// tone is simply skipped.

interface ToneOpts {
  freq: number
  ms: number
  gain: number
  type?: OscillatorType
  // Also buzz the phone: 'good' = one short pulse, 'bad' = long double pulse.
  // Opt-in, so quiet acknowledgement tones never vibrate.
  buzz?: 'good' | 'bad'
}

export const BUZZ_GOOD: number = 40
export const BUZZ_BAD: number[] = [120, 60, 120]

// A loud shed drowns the beep; the hand still feels the buzz. iOS Safari has
// no vibrate, and some browsers throw without a user gesture: best-effort.
function buzz(kind: 'good' | 'bad'): void {
  try {
    navigator.vibrate?.(kind === 'bad' ? BUZZ_BAD : BUZZ_GOOD)
  } catch { /* no vibration — the beep still sounded */ }
}

// ONE AudioContext for the document, created on first use and kept.
//
// It used to be one per note, closed in osc.onended — but onended never fires
// on a context the browser has suspended for autoplay, so every tone before
// the page's first touch leaked one. Browsers cap a document at a handful
// (~6) of contexts; past that, construction throws and the device goes
// silent for good. A kiosk that plays several notes per scan reaches that in
// a morning. Reusing one context also removes the per-note construction cost.
let sharedCtx: AudioContext | null = null

function audioContext(): AudioContext | null {
  if (sharedCtx) return sharedCtx
  const w = window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }
  const Ctor = w.AudioContext ?? w.webkitAudioContext
  if (!Ctor) return null
  sharedCtx = new Ctor()
  return sharedCtx
}

export function playTone({ freq, ms, gain, type = 'sine', buzz: kind }: ToneOpts): void {
  if (kind) buzz(kind)
  try {
    const ctx = audioContext()
    if (!ctx) return
    // Suspended until the page has had a user gesture. Resuming is free once
    // it is already running, and the tone is simply skipped while it is not.
    void ctx.resume().catch(() => { /* stays suspended — tone skipped */ })
    const osc = ctx.createOscillator()
    const g = ctx.createGain()
    osc.type = type
    osc.frequency.value = freq
    g.gain.value = gain
    osc.connect(g)
    g.connect(ctx.destination)
    osc.start()
    osc.stop(ctx.currentTime + ms / 1000)
    // Release the graph, never the context — see above.
    osc.onended = () => { try { osc.disconnect(); g.disconnect() } catch { /* already gone */ } }
  } catch { /* no WebAudio — the tone is optional */ }
}

// The camera badge scan's answer to the DT50S imager's decode beep: short,
// high and loud enough for a packline. Fired on successful decode only —
// recognising WHOSE badge it is stays the caller's problem.
export function playScanBeep(): void {
  playTone({ freq: 2400, ms: 90, gain: 0.25, type: 'square', buzz: 'good' })
}

// The clocking kiosk's two answers, told apart by ear (Anchor, 2026-09-08).
//
// WHY A SECOND SOUND. The kiosk beeped the same note whichever way it clocked
// someone, so a worker who did not stop to read the screen could not tell an
// IN from an OUT — and the kiosk is left on a forced direction all day. A
// night sprayer at Anchor clocked IN twice, hours apart, and the day paid
// against two arrivals and no departure. "Then that guy hears it and knows,
// hey, I clocked out now, I must clock in" — the site's own words.
//
// RISING for in, FALLING for out. The direction of the pitch IS the message:
// it needs no learning, survives a noisy packline, and works for someone who
// cannot read the screen from where they stand.
export function playClockTone(direction: 'IN' | 'OUT'): void {
  const [first, second] = direction === 'OUT' ? [2200, 1400] : [1400, 2200]
  playTone({ freq: first, ms: 100, gain: 0.25, type: 'square' })
  try {
    setTimeout(() => playTone({ freq: second, ms: 160, gain: 0.25, type: 'square' }), 110)
  } catch { /* no timers — the first note still sounded */ }
}

// Three low pulses under the confirmation: this scan repeated the direction
// the same worker last scanned. Deliberately unlike either clock tone — it
// says "something is wrong", not "in" or "out".
export function playRepeatWarning(): void {
  for (let i = 0; i < 3; i++) {
    try {
      setTimeout(() => playTone({ freq: 320, ms: 90, gain: 0.3, type: 'square' }), i * 150)
    } catch { /* no timers */ }
  }
}

// A worker whose END DATE HAS PASSED signed in on a device (Jason, 5 Oct 2026).
// Two notes, low-high-low — unlike the "yes" note, the rising/falling clock
// tones and the three low repeat pulses — so a foreman scanning fast hears
// "check this one" without reading the screen. Sign-in still succeeds.
export function playLeaverTone(): void {
  const notes = [700, 1050, 700]
  notes.forEach((freq, i) => {
    try {
      setTimeout(() => playTone({ freq, ms: 110, gain: 0.25, type: 'square' }), i * 140)
    } catch { /* no timers */ }
  })
}
